import React from 'react';

import { margin, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { SettingSection } from '@lumapps/designer-widget-settings/components/SettingSection';
import { mdiTune } from '@lumapps/lumx/icons';
import {
    Alignment,
    Button,
    ColorPalette,
    ColorVariant,
    Divider,
    Emphasis,
    FlexBox,
    Heading,
    Orientation,
    Size,
    Switch,
    Text,
    Typography,
} from '@lumapps/lumx/react';
import type { InstalledExtensionAPIProps } from '@lumapps/marketplace/legacy-types';
import { MicroAppPickerDialog } from '@lumapps/micro-app-pickers/components/MicroAppPickerDialog';
import SetupFormDialog from '@lumapps/micro-app-pickers/components/SetupFormDialog/SetupFormDialog';
import { useMicroApp } from '@lumapps/micro-apps/hooks/useMicroApp';
import type { MicroAppsInputVariables } from '@lumapps/micro-apps/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { WIDGET_BASE } from '@lumapps/widget-base/keys';

import { SCOPE } from '../../constants';
import { WIDGET_MICRO_APP } from '../../keys';
import { WidgetMicroAppSettingsProps } from '../../types';
import { MicroAppPreview } from './MicroAppPreview';

import './index.scss';

const CLASSNAME = 'widget-micro-app-settings';

/**
 * React component adding custom settings to the micro-app core widget
 * See: `apps/legacy/client/front-office/modules/content/modules/widget/modules/widget-micro-app/`
 * This component displays:
 * - If the extensionId is provided: `MicroAppPreview``
 * - Else, a button to open `MicroAppPicker`
 * @param WidgetMicroAppSettingsProps
 * @returns WidgetMicroAppSettings
 */
export const WidgetMicroAppSettings: React.FC<WidgetMicroAppSettingsProps> = ({ properties, onChange }) => {
    const { get } = useDataAttributes(SCOPE);
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();

    const { extensionId, extensionName, hasIcon, hasTitle, hasRefreshButton } = properties || {};
    const displaySettings = React.useMemo(
        () => ({
            hasIcon: hasIcon ?? true,
            hasTitle: hasTitle ?? true,
            hasRefreshButton: hasRefreshButton ?? true,
        }),
        [hasIcon, hasRefreshButton, hasTitle],
    );

    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [isSetupDialogOpen, toggleSetupDialog] = useBooleanState(false);

    const { data: microapp } = useMicroApp(extensionId || '', {
        enabled: Boolean(extensionId),
    });

    const handleConfirm = React.useCallback(
        (microApps: InstalledExtensionAPIProps[], settings?: MicroAppsInputVariables) => {
            const { id, extension } = microApps[0];

            onChange({
                extensionIcon: extension?.icon,
                extensionId: id,
                extensionName: extension?.name,
                thumbnail: extension?.medias?.previewImage,
                ...displaySettings,
                inputVariables: settings,
            });
            setIsOpen(false);
        },
        [displaySettings, onChange],
    );

    return (
        <>
            {/* micro-app selection section */}
            <SettingSection
                className={block([margin('vertical', Size.huge)])}
                title={translateKey(WIDGET_MICRO_APP.MICRO_APP_SELECTION)}
            >
                {extensionId ? (
                    <FlexBox vAlign={Alignment.center} hAlign={Alignment.center} orientation={Orientation.vertical}>
                        <MicroAppPreview properties={properties} onChange={onChange} />

                        {microapp?.inputVariables && microapp.inputVariables.length > 0 && (
                            <Button
                                className={element('settings-button', undefined, margin('top', Size.regular))}
                                emphasis={Emphasis.medium}
                                leftIcon={mdiTune}
                                onClick={toggleSetupDialog}
                            >
                                {translateKey(GLOBAL.SETTINGS)}
                            </Button>
                        )}

                        {microapp ? (
                            <SetupFormDialog
                                componentSettings={microapp?.componentSettings}
                                extensionName={extensionName || { en: '' }}
                                inputVariables={properties?.inputVariables}
                                inputVariablesDefinitions={microapp.inputVariables}
                                isOpen={isSetupDialogOpen}
                                onCancel={toggleSetupDialog}
                                onClose={toggleSetupDialog}
                                onSubmit={(newInputVariables) => {
                                    onChange({
                                        ...properties,
                                        ...displaySettings,
                                        inputVariables: newInputVariables,
                                    });
                                    toggleSetupDialog();
                                }}
                            />
                        ) : null}
                    </FlexBox>
                ) : (
                    <FlexBox
                        gap={Size.big}
                        orientation={Orientation.vertical}
                        hAlign={Alignment.center}
                        vAlign={Alignment.center}
                    >
                        <FlexBox vAlign={Alignment.center} hAlign={Alignment.center} orientation={Orientation.vertical}>
                            <Heading as="h2" typography={Typography.subtitle1}>
                                {translateKey(WIDGET_MICRO_APP.WIDGET_NOT_CONFIGURED)}
                            </Heading>

                            <Text as="p" typography={Typography.body1} style={{ textAlign: 'center' }}>
                                {translateKey(WIDGET_MICRO_APP.CONFIGURATION_INSTRUCTIONS)}
                            </Text>
                        </FlexBox>

                        <Button emphasis={Emphasis.medium} onClick={() => setIsOpen(!isOpen)}>
                            {translateKey(GLOBAL.SELECT)}
                        </Button>

                        <MicroAppPickerDialog
                            inputVariables={properties?.inputVariables}
                            isMultipleSelect={false}
                            isOpen={isOpen}
                            onCancel={() => setIsOpen(false)}
                            onConfirm={(microApps, settings) => {
                                handleConfirm(microApps, settings);
                            }}
                            onVisibilityChange={(open) => {
                                if (!open) {
                                    setIsOpen(false);
                                }
                            }}
                            canSetupInputVariables
                        />
                    </FlexBox>
                )}
            </SettingSection>

            {/* micro-app information displayed */}
            {extensionId ? (
                <>
                    <Divider className={margin('vertical', Size.regular)} />

                    <SettingSection
                        className={block([margin('top', Size.huge)])}
                        title={translateKey(WIDGET_BASE.SETTINGS_INFORMATION_DISPLAYED_TITLE)}
                    >
                        <FlexBox orientation={Orientation.vertical} gap={Size.big}>
                            <Heading
                                as="h3"
                                color={ColorPalette.dark}
                                colorVariant={ColorVariant.L2}
                                typography={Typography.overline}
                            >
                                {translateKey(WIDGET_MICRO_APP.MICRO_APP_HEADER)}
                            </Heading>

                            <Switch
                                isChecked={hasIcon}
                                onChange={(isChecked) => onChange({ hasIcon: isChecked })}
                                {...get({ element: 'switch', action: 'toggle-icon' })}
                            >
                                {translateKey(GLOBAL.ICON)}
                            </Switch>

                            <Switch
                                isChecked={hasTitle}
                                onChange={(isChecked) => onChange({ hasTitle: isChecked })}
                                {...get({ element: 'switch', action: 'toggle-title' })}
                            >
                                {translateKey(GLOBAL.TITLE)}
                            </Switch>

                            <Switch
                                isChecked={hasRefreshButton}
                                onChange={(isChecked) => onChange({ hasRefreshButton: isChecked })}
                                {...get({ element: 'switch', action: 'toggle-refresh-button' })}
                            >
                                {translateKey(WIDGET_MICRO_APP.REFRESH_BUTTON)}
                            </Switch>
                        </FlexBox>
                    </SettingSection>
                </>
            ) : null}
        </>
    );
};
